import { z } from 'zod';
import { optionalE164PhoneSchema } from './common/phone.js';

const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

export const DSR_REQUEST_TYPES = [
    'bilgi_talep', 'duzeltme', 'silme', 'sinirlandirma',
    'itiraz', 'tasima', 'profilleme_itiraz', 'diger',
];

export const DSR_STATUSES = ['yeni', 'kimlik_dogrulama', 'inceleniyor', 'beklemede', 'tamamlandi', 'reddedildi'];

export const DSR_CHANNELS = ['yazili', 'kep', 'eposta', 'noter', 'public_portal', 'diger'];

export const DSR_RESPONSE_TYPES = ['kabul', 'kismi_kabul', 'ret', 'bilgi_talep'];

const attachmentSchema = z.object({
    name: z.string().optional(),
    url: z.string().optional(),
});

export const DataSubjectRequestSchema = z.object({
    id: z.string().optional(),
    referenceNumber: z.string().optional(),
    sourceApplicationId: objectId.nullable().optional(),
    jurisdiction: z.enum(['kvkk', 'gdpr']).optional(),

    applicantName: z.string().min(1),
    applicantEmail: z.string().email().optional().or(z.literal('')),
    applicantPhone: optionalE164PhoneSchema,
    applicantIdentity: z.string().optional(),
    applicantAddress: z.string().optional(),

    channel: z.enum(DSR_CHANNELS).optional(),
    requestType: z.enum(DSR_REQUEST_TYPES),

    subject: z.string().optional(),
    description: z.string().optional(),
    attachments: z.array(attachmentSchema).optional(),

    identityVerification: z.object({
        verified: z.boolean().optional(),
        method: z.string().optional(),
        evidenceUrl: z.string().optional(),
        verifiedAt: z.string().or(z.date()).optional(),
        verifiedBy: objectId.optional(),
    }).optional(),

    receivedAt: z.string().or(z.date()).optional(),
    deadline: z.string().or(z.date()).optional(),
    respondedAt: z.string().or(z.date()).optional(),

    extension: z.object({
        extended: z.boolean().optional(),
        extendedUntil: z.string().or(z.date()).optional(),
        reason: z.string().optional(),
        applicantNotifiedAt: z.string().or(z.date()).optional(),
    }).optional(),

    assignee: objectId.nullable().optional(),
    unit: objectId.nullable().optional(),

    responseType: z.enum(DSR_RESPONSE_TYPES).optional(),
    responseText: z.string().optional(),
    responseAttachments: z.array(attachmentSchema).optional(),

    fee: z.object({
        charged: z.boolean().optional(),
        amount: z.number().optional(),
        currency: z.preprocess(
            (v) => (v === '' || v == null ? undefined : String(v).trim().toUpperCase()),
            z.string().max(10).optional(),
        ),
        note: z.string().optional(),
    }).optional(),

    disposalRecordId: objectId.nullable().optional(),

    status: z.enum(DSR_STATUSES).default('yeni'),

    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
});

export const CreateDataSubjectRequestSchema = DataSubjectRequestSchema.omit({ id: true, createdAt: true, updatedAt: true });
export const UpdateDataSubjectRequestSchema = DataSubjectRequestSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();

/**
 * Public portal intake (Veri Sorumlusuna Başvuru Tebliği m.5/2 zorunlu alanlar):
 * ad-soyad, TCKN (TR vatandaşı) / pasaport-kimlik no (yabancı), tebligata esas
 * adres veya e-posta, talep konusu.
 */
export const PublicDataSubjectRequestSchema = z.object({
    applicantName: z.string().min(3),
    applicantIdentity: z.string().min(5),
    applicantEmail: z.string().email().optional().or(z.literal('')),
    applicantAddress: z.string().optional(),
    applicantPhone: optionalE164PhoneSchema,
    requestType: z.enum(DSR_REQUEST_TYPES),
    subject: z.string().min(3),
    description: z.string().optional(),
    jurisdiction: z.enum(['kvkk', 'gdpr']).optional(),
    attachments: z.array(attachmentSchema).optional(),
}).refine(
    (data) => Boolean(data.applicantEmail?.trim()) || Boolean(data.applicantAddress?.trim()),
    { message: 'E-posta veya tebligata esas adres zorunludur', path: ['applicantEmail'] },
);
